'use client';

import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import { useMemo } from 'react';

import Link from 'next/link';

import { PropsWithClassname } from '@/app/types';
import { formatPriceInCents } from '@/app/utils/formatting';
import { ProjectPhoto, SubprojectPhoto } from '@/app/api';

import ProgressBar from '@/app/components/ProgressBar/ProgressBar';

const isImageProjectPhoto = (image: ProjectPhoto | SubprojectPhoto): image is ProjectPhoto =>
  !!(image as ProjectPhoto).width;

interface ProjectCardProps extends PropsWithClassname {
  title: string;
  href: string;
  image?: ProjectPhoto | SubprojectPhoto;
  currentAmountInCents: number;
  targetAmountInCents?: number;
  donationsCount: number;
}

const ProjectCard = ({
  title,
  href,
  image,
  currentAmountInCents,
  targetAmountInCents,
  donationsCount,
  className,
}: ProjectCardProps) => {
  const { t } = useTranslation();

  const imageAlt = useMemo(() => {
    if (!image) {
      return title;
    }

    if (!isImageProjectPhoto(image)) {
      return title;
    }

    return image.description ?? title;
  }, [image, title]);

  return (
    <Link
      href={href}
      prefetch={process.env.NEXT_PUBLIC_PREFETCH_LINKS === 'true'}
      className={classNames(
        className,
        'group flex flex-col w-[268px] h-[368px] grow-0 shrink-0 bg-white rounded border border-neutral-light shadow-md hover:shadow-xl overflow-hidden transition-all duration-500',
      )}
    >
      {!!image?.sizes.miniCropped.path && (
        <span className="block w-full h-[168px] overflow-hidden">
          <img
            width={0}
            height={0}
            src={image?.sizes.miniCropped.path}
            alt={imageAlt}
            sizes="268px"
            className="w-[268px] h-[168px] scale-1 group-hover:scale-110 transition-all linear duration-[3000ms]"
          />
        </span>
      )}
      <span className="flex flex-col justify-between p-[20px] grow">
        <h3 className="mb-[20px] text-[24px] leading-[32px] font-medium text-balance line-clamp-2 ellipsis">{title}</h3>
        <span className="block">
          <ProgressBar
            total={targetAmountInCents ?? currentAmountInCents}
            currentValue={currentAmountInCents}
            className="mb-[12px]"
            indicateFinishedWithPrimaryColor={!targetAmountInCents}
          />
          <span className="block font-medium break-words text-[16px]">
            {t('components.ProjectCard.collected', { amount: formatPriceInCents(currentAmountInCents) })}
          </span>
          <span className="block break-words text-neutral-semiTransparent text-[14px]">
            {targetAmountInCents
              ? t('components.ProjectCard.ofTotal', { amount: formatPriceInCents(targetAmountInCents) })
              : t('components.ProjectCard.fromDonors', { count: donationsCount })}
          </span>
        </span>
      </span>
    </Link>
  );
};

export default ProjectCard;
